import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Connects to data-controller="file-drop"
// Drag-and-drop file upload for documents. Fully reusable — works for any
// documentable parent by accepting configurable values.
//
// Usage:
//   <div data-controller="file-drop"
//        data-file-drop-url-value="/companies/1/documents"
//        data-action="dragenter->file-drop#dragEnter
//                     dragover->file-drop#dragOver
//                     dragleave->file-drop#dragLeave
//                     drop->file-drop#drop">
//     <div data-file-drop-target="overlay" class="hidden ...">
//       Drop files to upload
//     </div>
//     ...existing content...
//   </div>
export default class extends Controller {
  static targets = ["overlay", "fileInput"]

  static values = {
    url: String,
    paramName: { type: String, default: "document" },
    fileField: { type: String, default: "file" },
    documentType: { type: String, default: "unclassified" }
  }

  initialize() {
    this.resetUploadProgress()
  }

  connect() {
    this.connected = true
    this.resetDragState()
  }

  disconnect() {
    this.connected = false
    this.resetDragState()
  }

  dragEnter(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter++
    this.showOverlay()
  }

  dragOver(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
  }

  dragLeave(event) {
    event.preventDefault()
    if (!this.isFileDrag(event)) return

    this.dragCounter = Math.max(0, this.dragCounter - 1)
    if (this.dragCounter === 0) {
      this.hideOverlay()
    }
  }

  drop(event) {
    if (!this.isFileDrag(event)) return
    event.preventDefault()
    this.resetDragState()

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.uploadFiles(files)
  }

  isFileDrag(event) {
    return event.dataTransfer?.types?.includes("Files")
  }

  browse() {
    this.fileInputTarget.click()
  }

  fileSelected() {
    const files = Array.from(this.fileInputTarget.files)
    if (files.length === 0) return

    this.uploadFiles(files)
    this.fileInputTarget.value = ""
  }

  uploadFiles(files) {
    this.totalUploads += files.length
    this.pendingUploads += files.length
    this.showProgress(this.completedUploads, this.totalUploads)

    const uploads = files.map(async file => {
      try {
        this.streamMessages.push(await this.uploadFile(file))
      } catch {
        this.failedUploads++
      } finally {
        this.completedUploads++
        this.pendingUploads--
        this.showProgress(this.completedUploads, this.totalUploads)

        if (this.pendingUploads === 0) this.finishUploads()
      }
    })

    return Promise.all(uploads)
  }

  finishUploads() {
    const total = this.totalUploads
    const failed = this.failedUploads
    const streamMessages = this.streamMessages.join("")

    try {
      if (this.connected) Turbo.renderStreamMessage(streamMessages)
    } finally {
      this.hideOverlay()
      this.resetUploadProgress()
    }

    const succeeded = total - failed
    const noun = this.documentTypeValue === "photo" ? "photo" : "document"

    if (succeeded > 0) {
      this.showToast("success", `${succeeded} ${noun}${succeeded === 1 ? "" : "s"} uploaded`)
    }

    if (failed > 0) {
      this.showToast("error", `${failed} ${noun}${failed === 1 ? "" : "s"} failed to upload`)
    }
  }

  resetUploadProgress() {
    this.totalUploads = 0
    this.pendingUploads = 0
    this.completedUploads = 0
    this.failedUploads = 0
    this.streamMessages = []
  }

  async uploadFile(file) {
    const formData = new FormData()
    formData.append(`${this.paramNameValue}[${this.fileFieldValue}]`, file)
    if (this.documentTypeValue !== "photo") {
      formData.append(`${this.paramNameValue}[document_type]`, "unclassified")
    }

    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`)
    }

    return response.text()
  }

  resetDragState() {
    this.dragCounter = 0
    this.hideOverlay()
  }

  showOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
  }

  hideOverlay() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
  }

  showProgress(completed, total) {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("hidden")
    const label = this.overlayTarget.querySelector("[data-role='label']")
    if (label) {
      label.textContent = (completed < total)
        ? `Uploading ${completed + 1} of ${total}...`
        : "Processing..."
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta?.content || ""
  }
}
